import type { EditorialContent } from '@cobalt/utilities';
import { getVideoUrl } from '@services/LinkRuleService';
import matchHighlightServiceInstance from '@services/MatchHighlightsService';
import matchUnitServiceInstance from '@services/MatchUnitService';
import type { MediaAssetServiceCallback } from '@services/MediaAssetService';
import mediaAssetServiceInstance from '@services/MediaAssetService';
import type { Match } from '@shared-types/match.types';
import { MatchStatus } from '@shared-types/match.types';
import type { MediaAssetServiceResponse } from '@shared-types/mediaAssets.types';
import { MediaAssetServiceStatus } from '@shared-types/mediaAssets.types';
import { getCountdownInterval } from '@utilities/countdown-interval';

export default class MatchHighLightPlugin {
  mu: HTMLElement;
  registeredVideos: {
    [videoId: string]: {
      videoTitle: string;
      videoHref: string;
    };
  } = {};
  translations: any;
  options: any;
  dom: HTMLElement;

  constructor(dom: HTMLElement, options: any) {
    const defaults = {
      matchOptions: {},
    };

    this.options = {
      ...defaults,
      ...options,
    };
    this.translations = this.options?.translations?.availability;

    this.registeredVideos = {};
    this.dom = dom;
    this.mu = this.dom.closest<HTMLElement>('.js-pk-match-unit');
  }

  get matchIds(): string[] {
    if (!this.options.matchOptions.match?.id && !this.options.matchOptions.matchIds) return null;

    return this.options.matchOptions.matchIds?.length
      ? this.options.matchOptions.matchIds
      : [this.options.matchOptions.match?.id];
  }

  off() {
    const regVideoIds = Object.keys(this.registeredVideos);

    this.matchIds?.length && matchHighlightServiceInstance.off(this.matchIds, this.attach, this);
    regVideoIds.length && mediaAssetServiceInstance.off(regVideoIds, this.check, this);

    if (this.matchIds.length === 1) {
      const match = this.options.matchOptions.match;
      matchUnitServiceInstance.off({ match }, this.checkMatch, this);
    }
  }

  init() {
    if (!this.matchIds?.length) return;

    if (this.matchIds.length > 1) {
      matchHighlightServiceInstance.on(this.matchIds, this.attach, this);
    } else {
      const match = this.options.matchOptions.match;
      matchUnitServiceInstance.on({ match }, this.checkMatch, this);
    }
  }

  checkMatch = (match: Match) => {
    if (match?.status !== MatchStatus.FINISHED) {
      this.toggleSlot(false);
      return;
    }

    matchHighlightServiceInstance.on(this.matchIds, this.attach, this);
  };

  toggleSlot(visible: boolean) {
    this.dom.slot = visible ? 'cta' : '';
  }

  attach = (video: EditorialContent) => {
    if (!video) {
      this.toggleSlot(false);
      return;
    }

    this.registeredVideos[video.id] = {
      videoHref: getVideoUrl(video.id),
      videoTitle: video?.title ?? '',
    };

    const attachedVideos = Object.keys(this.registeredVideos);

    mediaAssetServiceInstance.on(attachedVideos, this.check, this);
  };

  check: MediaAssetServiceCallback = (videos = []) => {
    const regVideos = Object.keys(this.registeredVideos);
    let available = false;

    if (this.matchIds.length < 2) {
      const singleVideo = this.registeredVideos[regVideos[0]];
      available = this.singleAvailability(videos, regVideos[0]);
      this.singleUpdate(available, singleVideo.videoTitle, singleVideo.videoHref);
    } else {
      available = this.multiAvailability(videos, regVideos);
      const [highlights] = videos;
      const { availableAt } = highlights ?? {};
      if (availableAt) this._setCountdown(availableAt);
    }

    this.dom.classList.toggle('video-available', available);
    this.dom.classList.toggle('video-not-available', !available);
    this.toggleSlot(available);
  };

  singleAvailability = (videos: MediaAssetServiceResponse[], videoId: string) => {
    const findVideo = videos?.find((v) => v.mediaAssetId === `${videoId}`);
    return findVideo?.status === MediaAssetServiceStatus.AVAILABLE;
  };

  multiAvailability = (videos: MediaAssetServiceResponse[], videoIds: string[]) =>
    videos
      .filter((v) => videoIds.includes(v.mediaAssetId))
      .every((v) => v.status === MediaAssetServiceStatus.AVAILABLE);

  singleUpdate = (available: boolean, videoTitle: string, videoHref: string) => {
    if (this.mu) {
      this.mu.classList.toggle('mu--with-highlights', available);
      this.mu.dataset.hasHighlights = available ? 'true' : 'false';
    }
    this._updateMuParentTracking(available);

    this.dom.title = videoTitle;
    this.dom.setAttribute('href', videoHref);
    this.dom.setAttribute('link-href', videoHref);
    this.dom.setAttribute('aria-label', videoTitle);
  };

  _updateMuParentTracking = (isAvailable: boolean) => {
    const trackedParent = this.dom.closest<HTMLElement>('.match-row_link');
    if (!trackedParent?.dataset?.tracking) return;

    const tracking = JSON.parse(trackedParent.dataset.tracking) as { variant: string };
    if (tracking.variant?.startsWith('post')) {
      tracking.variant = isAvailable ? 'post with highlights' : 'post';
      trackedParent.dataset.tracking = JSON.stringify(tracking);
    }
  };

  _setCountdown = (availableAt: string) => {
    const textContainer = this.dom.querySelector('.js-video-not-available');
    if (textContainer) {
      const { hours, minutes } = getCountdownInterval(availableAt);
      let translatedText = '';

      if (hours > 0) {
        translatedText = this.translations?.hours?.replace('{hours}', hours).replace('{minutes}', minutes);
      } else if (minutes > 0) {
        translatedText = this.translations?.minutes?.replace('{minutes}', minutes);
      } else {
        translatedText = this.translations?.zero;
      }

      textContainer.innerHTML = translatedText;
    }
  };
}
